import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import api from "@/lib/api";

/**
 * Bell icon + popover for the current user's in-app notifications.
 * Polls `/notifications` every 60s, on-mount, and when the popover opens so
 * the unread badge stays current without any WebSocket infra.
 */
export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const popoverRef = useRef(null);
  const buttonRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 60_000);
    return () => clearInterval(iv);
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (
        popoverRef.current && !popoverRef.current.contains(e.target) &&
        buttonRef.current && !buttonRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const toggle = () => {
    setOpen((v) => !v);
    if (!open) load();
  };

  const markOneRead = async (n) => {
    if (n.is_read) return;
    // Optimistic update — instant feedback, roll back if the request fails.
    setItems((prev) => prev.map((x) => (x.notification_id === n.notification_id ? { ...x, is_read: true } : x)));
    setUnread((u) => Math.max(0, u - 1));
    try {
      await api.post(`/notifications/${n.notification_id}/read`);
    } catch {
      load(); // roll back to server truth
    }
  };

  const markAll = async () => {
    if (unread === 0) return;
    setItems((prev) => prev.map((x) => ({ ...x, is_read: true })));
    setUnread(0);
    try {
      await api.post("/notifications/read-all");
    } catch {
      load();
    }
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={toggle}
        aria-label="Notifications"
        aria-expanded={open}
        className="relative inline-flex items-center justify-center w-9 h-9 rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-sg-gold text-sg-navy text-[10px] font-bold inline-flex items-center justify-center"
            data-testid="notification-unread-badge"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={popoverRef}
          className="absolute right-0 mt-2 w-80 sm:w-96 max-h-[70vh] overflow-hidden rounded-2xl border border-sg-line bg-white shadow-xl z-50 flex flex-col"
          data-testid="notification-popover"
        >
          <div className="px-4 py-3 border-b border-sg-line flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-sg-navy">Notifications</div>
              <div className="text-[11px] text-sg-mute">
                {unread > 0 ? `${unread} unread` : "You're all caught up"}
              </div>
            </div>
            {unread > 0 && (
              <button
                type="button"
                onClick={markAll}
                className="text-xs text-sg-gold-deep hover:text-sg-navy inline-flex items-center gap-1"
                data-testid="notification-mark-all-read"
              >
                <CheckCheck className="w-3.5 h-3.5" /> Mark all read
              </button>
            )}
          </div>

          <div className="overflow-y-auto divide-y divide-sg-line" data-testid="notification-list">
            {loading && items.length === 0 && (
              <div className="px-4 py-10 text-center text-sm text-sg-mute">Loading…</div>
            )}
            {!loading && items.length === 0 && (
              <div className="px-4 py-10 text-center text-sm text-sg-mute" data-testid="notification-empty">
                No notifications yet. When new project media hits your locality, you&apos;ll see it here.
              </div>
            )}
            {items.map((n) => (
              <Link
                key={n.notification_id}
                to={n.link || "#"}
                onClick={() => { markOneRead(n); setOpen(false); }}
                className={`block px-4 py-3 hover:bg-sg-cream-soft transition-colors ${
                  !n.is_read ? "bg-sg-cream-soft/50" : ""
                }`}
                data-testid={`notification-item-${n.notification_id}`}
              >
                <div className="flex items-start gap-2">
                  {!n.is_read && (
                    <span className="w-2 h-2 rounded-full bg-sg-gold mt-1.5 shrink-0" aria-hidden />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-sg-navy truncate">{n.title}</div>
                    <div className="text-xs text-sg-mute mt-0.5">{n.body}</div>
                    <div className="text-[10px] text-sg-mute font-mono mt-1">
                      {n.locality} · {new Date(n.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
